import React from 'react';
import { ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import { LoanOfficer } from '../data/mockData';
import { formatCurrency, formatPercent, formatMinutes, getPerformanceColor } from '../utils/helpers';
import { DateRange, isCountMode } from './DashboardHeader';
export type SortConfig = {
  key: keyof LoanOfficer;
  direction: 'asc' | 'desc';
};
interface PerformanceTableProps {
  loanOfficers: LoanOfficer[];
  sortConfig: SortConfig;
  onSort: (key: keyof LoanOfficer) => void;
  onSelectLO: (lo: LoanOfficer) => void;
  dateRange: DateRange;
}
export function PerformanceTable({
  loanOfficers,
  sortConfig,
  onSort,
  onSelectLO,
  dateRange
}: PerformanceTableProps) {
  const showCounts = isCountMode(dateRange);
  const renderSortIcon = (key: keyof LoanOfficer) => {
    if (sortConfig.key !== key) return <ChevronDown className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-119" />;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-gray-700" data-id="element-120" /> : <ChevronDown className="w-4 h-4 text-gray-700" data-id="element-121" />;
  };
  const renderHeader = (label: string, key: keyof LoanOfficer) => <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-50 transition-colors" onClick={() => onSort(key)} data-id="element-122">
      <div className="flex items-center gap-1" data-id="element-123">
        {label}
        {renderSortIcon(key)}
      </div>
    </th>;
  const renderMetricCell = (value: number, metricKey: string, isCurrency = false, isPercent = false, isTime = false) => {
    const color = getPerformanceColor(metricKey, value);
    const dotColor = color === 'green' ? 'bg-green-500' : color === 'yellow' ? 'bg-amber-500' : 'bg-red-500';
    let displayValue = value.toString();
    if (isCurrency) displayValue = formatCurrency(value);
    if (isPercent) displayValue = formatPercent(value);
    if (isTime) displayValue = formatMinutes(value);
    return <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-124">
        <div className="flex items-center gap-2" data-id="element-125">
          <span className={`w-2 h-2 rounded-full ${dotColor}`} data-id="element-126"></span>
          {displayValue}
        </div>
      </td>;
  };
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-127">
      <div className="overflow-x-auto" data-id="element-128">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-129">
          <thead className="bg-gray-50" data-id="element-130">
            <tr data-id="element-131">
              {renderHeader('Loan Officer', 'name')}
              {renderHeader('Five9 Calls/Day', 'five9CallsPerDay')}
              {renderHeader('Five9 Talk Time', 'five9TalkTime')}
              {renderHeader('RC Calls/Day', 'ringCentralCallsPerDay')}
              {renderHeader('RC Talk Time', 'ringCentralTalkTime')}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100/60" data-id="element-132">
                <div className="flex items-center gap-1" data-id="element-133">Total Calls/Day</div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100/60" data-id="element-134">
                <div className="flex items-center gap-1" data-id="element-135">Total Talk Time</div>
              </th>
              {renderHeader('Conversations', 'conversations')}
              {showCounts ? <>
                  {renderHeader('# Credit Pulls', 'creditPulls')}
                  {renderHeader('# Applications', 'applications')}
                  {renderHeader('# Submissions', 'submissions')}
                  {renderHeader('# Signed', 'signed')}
                  {renderHeader('# Locks', 'locks')}
                  {renderHeader('# Fundings', 'fundings')}
                </> : <>
                  {renderHeader('Pull→App %', 'pullToAppPct')}
                  {renderHeader('App→Submit %', 'appToSubmissionPct')}
                  {renderHeader('Submit→Signed %', 'submissionToSignedPct')}
                  {renderHeader('Submit→Lock %', 'submissionToLockPct')}
                  {renderHeader('Signed→Fund %', 'signedToFundPct')}
                </>}
              {renderHeader('Locked Pipeline', 'lockedPipelineValue')}
              {renderHeader('Pipeline Value', 'pipelineValue')}
              {renderHeader('Fallout %', 'falloutPct')}
              {renderHeader('Follow-Up %', 'followUpCompliancePct')}
              {renderHeader('Suspended %', 'suspendedFilePct')}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-136">
            {loanOfficers.map(lo => {
            const isFlagged = lo.coachingFlags.length > 0;
            return <tr key={lo.id} onClick={() => onSelectLO(lo)} className={`cursor-pointer transition-colors hover:bg-gray-50 ${isFlagged ? 'bg-red-50/30' : ''}`} data-id="element-137">
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-138">
                    <div className="flex items-center" data-id="element-139">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-xs" data-id="element-140">
                        {lo.initials}
                      </div>
                      <div className="ml-3 flex items-center gap-2" data-id="element-141">
                        <div className="text-sm font-medium text-gray-900" data-id="element-142">
                          {lo.name}
                        </div>
                        {isFlagged && <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-143" />}
                      </div>
                    </div>
                  </td>
                  {renderMetricCell(lo.five9CallsPerDay, 'five9CallsPerDay')}
                  {renderMetricCell(lo.five9TalkTime, 'five9TalkTime', false, false, true)}
                  {renderMetricCell(lo.ringCentralCallsPerDay, 'ringCentralCallsPerDay')}
                  {renderMetricCell(lo.ringCentralTalkTime, 'ringCentralTalkTime', false, false, true)}
                  {renderMetricCell(lo.five9CallsPerDay + lo.ringCentralCallsPerDay, 'totalCallsPerDay')}
                  {renderMetricCell(lo.five9TalkTime + lo.ringCentralTalkTime, 'totalTalkTime', false, false, true)}
                  {renderMetricCell(lo.conversations, 'conversations')}
                  {showCounts ? <>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-144">
                        {lo.creditPulls}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-145">
                        {lo.applications}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-146">
                        {lo.submissions}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-147">
                        {lo.signed}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-148">
                        {lo.locks}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium" data-id="element-149">
                        {lo.fundings}
                      </td>
                    </> : <>
                      {renderMetricCell(lo.pullToAppPct, 'pullToAppPct', false, true)}
                      {renderMetricCell(lo.appToSubmissionPct, 'appToSubmissionPct', false, true)}
                      {renderMetricCell(lo.submissionToSignedPct, 'submissionToSignedPct', false, true)}
                      {renderMetricCell(lo.submissionToLockPct, 'submissionToLockPct', false, true)}
                      {renderMetricCell(lo.signedToFundPct, 'signedToFundPct', false, true)}
                    </>}
                  {renderMetricCell(lo.lockedPipelineValue, 'lockedPipelineValue', true, false)}
                  {renderMetricCell(lo.pipelineValue, 'pipelineValue', true, false)}
                  {renderMetricCell(lo.falloutPct, 'falloutPct', false, true)}
                  {renderMetricCell(lo.followUpCompliancePct, 'followUpCompliancePct', false, true)}
                  {renderMetricCell(lo.suspendedFilePct, 'suspendedFilePct', false, true)}
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}